import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Checkbox,
  Space,
  Spin,
  Input,
  message,
  Divider,
  Radio,
  Tag,
  Select,
  Tabs,
  Tooltip,
  Modal,
  Switch,
  Upload,
} from 'antd';
import {
  ArrowRightOutlined,
  SendOutlined,
  PlusOutlined,
  CloseOutlined,
  FilterOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import DataTable from '../components/DataTable';
import { getPreview, getColumns, getSheets, filterData, uploadFile } from '../services/api';
import type { ColumnInfo, PreviewData, FilterCondition } from '../types';

const OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'contains'];

const MISSING_STRATEGIES = [
  { value: 'none', label: '不处理' },
  { value: 'drop', label: '删除含缺失值的行' },
  { value: 'mean', label: '用均值填充' },
  { value: 'median', label: '用中位数填充' },
];

const SmartChartDataSelect: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileId = searchParams.get('fileId') || '';

  // Data state
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [columns, setColumns] = useState<ColumnInfo[]>([]);
  const [selectedCols, setSelectedCols] = useState<string[]>([]);

  // Variable selectors
  const [xAxis, setXAxis] = useState<string>('');
  const [yAxis, setYAxis] = useState<string>('');
  const [groupBy, setGroupBy] = useState<string>('');

  // Chat
  const [chatInput, setChatInput] = useState('');

  // Sheets
  const [sheets, setSheets] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');

  // Filters
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [newFilterCol, setNewFilterCol] = useState<string>('');
  const [newFilterOp, setNewFilterOp] = useState<string>('=');
  const [newFilterVal, setNewFilterVal] = useState<string>('');
  const [filterLoading, setFilterLoading] = useState(false);

  // Task 4: Outlier removal
  const [removeOutliers, setRemoveOutliers] = useState(false);
  const [outlierCount, setOutlierCount] = useState<number>(0);
  const [outlierCells, setOutlierCells] = useState<Set<string>>(new Set());

  // Task 6: Missing value handling
  const [missingStrategy, setMissingStrategy] = useState<string>('none');

  // Task 3: Re-upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Derived data
  const numericColumns = useMemo(
    () => columns.filter((c) => ['integer', 'float'].includes(c.dtype)),
    [columns]
  );
  const categoricalColumns = useMemo(
    () => columns.filter((c) => ['categorical', 'text'].includes(c.dtype)),
    [columns]
  );

  const columnTypes: Record<string, string> = useMemo(() => {
    const types: Record<string, string> = {};
    columns.forEach((c) => {
      types[c.name] = c.dtype;
    });
    return types;
  }, [columns]);

  // Task 6: Missing value stats
  const missingStats = useMemo(() => {
    if (!columns.length || !preview) return [];
    return columns.map((col) => {
      const total = preview.total_rows || preview.data.length;
      const missingCount = col.missing_rate !== undefined ? Math.round(col.missing_rate * total) : 0;
      const pct = col.missing_rate !== undefined ? (col.missing_rate * 100).toFixed(1) : '0.0';
      return { name: col.name, missing: missingCount, pct, total };
    });
  }, [columns, preview]);

  // Task 4: IQR outlier detection (frontend marking)
  useEffect(() => {
    if (!removeOutliers || !preview || !columns.length) {
      setOutlierCells(new Set());
      setOutlierCount(0);
      return;
    }
    const cells = new Set<string>();
    let count = 0;
    const numCols = columns.filter((c) => ['integer', 'float'].includes(c.dtype));
    for (const col of numCols) {
      const values = preview.data
        .map((row) => row[col.name])
        .filter((v) => v !== null && v !== undefined)
        .map(Number)
        .filter((v) => !isNaN(v));
      if (values.length < 4) continue;
      const sorted = [...values].sort((a, b) => a - b);
      const q1 = sorted[Math.floor(sorted.length * 0.25)];
      const q3 = sorted[Math.floor(sorted.length * 0.75)];
      const iqr = q3 - q1;
      const lower = q1 - 1.5 * iqr;
      const upper = q3 + 1.5 * iqr;
      preview.data.forEach((row, rowIdx) => {
        const v = Number(row[col.name]);
        if (!isNaN(v) && (v < lower || v > upper)) {
          cells.add(`${rowIdx}_${col.name}`);
          count++;
        }
      });
    }
    setOutlierCells(cells);
    setOutlierCount(count);
  }, [removeOutliers, preview, columns]);

  // Load sheets on mount
  useEffect(() => {
    if (!fileId) return;
    getSheets(fileId)
      .then((res) => {
        if (res.sheets && res.sheets.length > 0) {
          setSheets(res.sheets);
          setActiveSheet(res.sheets[0]);
        }
      })
      .catch(() => {
        // Not an Excel file or no sheets - that's fine
      });
  }, [fileId]);

  // Load data when fileId or activeSheet changes
  const loadData = useCallback(
    async (sheet?: string) => {
      setLoading(true);
      try {
        const sheetParam = sheet || (sheets.length > 0 ? activeSheet : undefined);
        const [prevData, colData] = await Promise.all([
          getPreview(fileId, sheetParam),
          getColumns(fileId, sheetParam),
        ]);
        setPreview(prevData);
        setColumns(colData);
        if (colData.length > 0) {
          setSelectedCols(colData.slice(0, Math.min(3, colData.length)).map((c) => c.name));
          setXAxis(colData[0].name);
          // Auto-select first numeric column as Y axis
          const firstNumeric = colData.find((c) =>
            ['integer', 'float'].includes(c.dtype)
          );
          setYAxis(firstNumeric ? firstNumeric.name : '');
          setGroupBy('');
        }
        setFilters([]);
      } catch {
        message.error('加载数据失败，请确认后端服务已启动');
      } finally {
        setLoading(false);
      }
    },
    [fileId, sheets.length, activeSheet]
  );

  useEffect(() => {
    if (fileId) loadData();
  }, [fileId, activeSheet, loadData]);

  // Apply filters
  const applyFilters = useCallback(
    async (newFilters: FilterCondition[]) => {
      if (newFilters.length === 0) {
        // Reset to unfiltered preview
        loadData();
        return;
      }
      setFilterLoading(true);
      try {
        const sheetParam = sheets.length > 0 ? activeSheet : undefined;
        const result = await filterData(fileId, newFilters, sheetParam, removeOutliers);
        setPreview(result);
      } catch {
        message.error('筛选失败');
      } finally {
        setFilterLoading(false);
      }
    },
    [fileId, sheets, activeSheet, loadData, removeOutliers]
  );

  const addFilter = () => {
    if (!newFilterCol || !newFilterVal.trim()) {
      message.warning('请填写完整的筛选条件');
      return;
    }
    const newFilter: FilterCondition = {
      column: newFilterCol,
      operator: newFilterOp,
      value: newFilterVal.trim(),
    };
    const updated = [...filters, newFilter];
    setFilters(updated);
    setFilterModalOpen(false);
    setNewFilterCol('');
    setNewFilterOp('=');
    setNewFilterVal('');
    applyFilters(updated);
  };

  const removeFilter = (index: number) => {
    const updated = filters.filter((_, i) => i !== index);
    setFilters(updated);
    applyFilters(updated);
  };

  // Task 1: Select All / Deselect All
  const allColNames = useMemo(() => columns.map((c) => c.name), [columns]);
  const isAllSelected = selectedCols.length === allColNames.length && allColNames.length > 0;
  const isIndeterminate = selectedCols.length > 0 && selectedCols.length < allColNames.length;

  const handleSelectAll = (checked: boolean) => {
    setSelectedCols(checked ? [...allColNames] : []);
  };

  // Task 3: Re-upload handler
  const handleReUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setUploadModalOpen(false);
      message.success('文件上传成功，正在加载新数据...');
      // Navigate to same page with new fileId
      setSearchParams({ fileId: result.file_id });
    } catch {
      message.error('上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (selectedCols.length === 0) {
      message.warning('请至少选择一列数据');
      return;
    }
    const params = encodeURIComponent(
      JSON.stringify({
        fileId,
        columns: selectedCols,
        xAxis,
        yAxis,
        groupBy: groupBy || undefined,
        sheet: activeSheet || undefined,
        filters: filters.length > 0 ? filters : undefined,
        removeOutliers: removeOutliers || undefined,
        missingStrategy: missingStrategy !== 'none' ? missingStrategy : undefined,
      })
    );
    navigate(`/smart-chart/type-select?params=${params}`);
  };

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const input = chatInput.trim().toLowerCase();
    const colNames = columns.map((c) => c.name);
    let matched = false;

    // Keyword maps for axis assignment
    const yKeywords = ['y轴', 'y 轴', '纵轴', '做y', '作为y', '当y'];
    const xKeywords = ['x轴', 'x 轴', '横轴', '做x', '作为x', '当x'];
    const groupKeywords = ['分组', '按照', '按', 'group'];
    const selectKeywords = ['选', '用', '添加', '加上', '使用', '分析'];
    const removeKeywords = ['去掉', '移除', '删除', '取消', '不要', '不用'];

    for (const colName of colNames) {
      const colLower = colName.toLowerCase();
      if (!input.includes(colLower)) continue;

      if (removeKeywords.some((kw) => input.includes(kw))) {
        setSelectedCols((prev) => prev.filter((c) => c !== colName));
        message.success(`已取消选中「${colName}」列`);
        matched = true;
      } else if (xKeywords.some((kw) => input.includes(kw))) {
        setXAxis(colName);
        if (!selectedCols.includes(colName)) {
          setSelectedCols((prev) => [...prev, colName]);
        }
        message.success(`已将「${colName}」设为 X 轴变量`);
        matched = true;
      } else if (yKeywords.some((kw) => input.includes(kw))) {
        setYAxis(colName);
        if (!selectedCols.includes(colName)) {
          setSelectedCols((prev) => [...prev, colName]);
        }
        message.success(`已将「${colName}」设为 Y 轴变量`);
        matched = true;
      } else if (groupKeywords.some((kw) => input.includes(kw))) {
        setGroupBy(colName);
        message.success(`已将「${colName}」设为分组变量`);
        matched = true;
      } else if (selectKeywords.some((kw) => input.includes(kw))) {
        if (!selectedCols.includes(colName)) {
          setSelectedCols((prev) => [...prev, colName]);
        }
        message.success(`已选中「${colName}」列`);
        matched = true;
      }

      if (matched) break;
    }

    if (!matched) {
      message.info(`未能识别列名，当前可用列：${colNames.join('、')}`);
    }

    setChatInput('');
  };

  // Column summary helper
  const getColumnSummary = (col: ColumnInfo): string => {
    if (['integer', 'float'].includes(col.dtype)) {
      if (col.min !== undefined && col.max !== undefined && col.mean !== undefined) {
        const missingPct = col.missing_rate !== undefined ? `${(col.missing_rate * 100).toFixed(1)}%缺失` : '';
        return `[${col.min}~${col.max}, \u03BC=${col.mean}${missingPct ? ', ' + missingPct : ''}]`;
      }
      return '';
    }
    if (['categorical', 'text'].includes(col.dtype)) {
      const topStr = col.top_values ? col.top_values.join(', ') : '';
      return `[${col.unique}种${topStr ? ': ' + topStr : ''}]`;
    }
    return '';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="正在加载数据预览..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24 }}>
      {/* Left: Data Preview */}
      <div style={{ flex: 1 }}>
        {/* Sheet Tabs */}
        {sheets.length > 1 && (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Tabs
              activeKey={activeSheet}
              onChange={(key) => setActiveSheet(key)}
              size="small"
              items={sheets.map((s) => ({ key: s, label: s }))}
            />
          </Card>
        )}

        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#061b31', fontWeight: 600 }}>数据预览</span>
              {/* Task 3: Re-upload button */}
              <Tooltip title="重新上传文件">
                <Button
                  icon={<UploadOutlined />}
                  size="small"
                  type="text"
                  style={{ color: '#7c4dff' }}
                  onClick={() => setUploadModalOpen(true)}
                />
              </Tooltip>
            </div>
          }
          style={{ marginBottom: 16 }}
        >
          {/* Filter Bar */}
          <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <Button
              icon={<PlusOutlined />}
              size="small"
              onClick={() => {
                if (columns.length > 0 && !newFilterCol) {
                  setNewFilterCol(columns[0].name);
                }
                setFilterModalOpen(true);
              }}
              style={{ borderColor: '#7c4dff', color: '#7c4dff' }}
            >
              添加筛选
            </Button>
            {filters.map((f, i) => (
              <Tag
                key={i}
                closable
                onClose={() => removeFilter(i)}
                color="purple"
                style={{ fontSize: 12 }}
              >
                <FilterOutlined style={{ marginRight: 4 }} />
                {f.column} {f.operator} {f.value}
              </Tag>
            ))}
            {filters.length > 0 && preview && (
              <span style={{ fontSize: 12, color: '#666' }}>
                筛选后 {preview.total_rows} 行
              </span>
            )}
          </div>

          {/* Task 4: Outlier removal toggle */}
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch
              size="small"
              checked={removeOutliers}
              onChange={(checked) => setRemoveOutliers(checked)}
            />
            <span style={{ fontSize: 13, color: '#666' }}>自动剔除异常值 (IQR 方法)</span>
            {removeOutliers && outlierCount > 0 && (
              <Tag color="red" style={{ fontSize: 12 }}>
                检测到 {outlierCount} 个异常值已标记
              </Tag>
            )}
          </div>

          {preview && (
            <DataTable
              columns={preview.columns}
              data={preview.data}
              totalRows={preview.total_rows}
              columnTypes={columnTypes}
              selectable
              selectedColumns={selectedCols}
              onColumnSelect={setSelectedCols}
              loading={filterLoading}
              highlightCells={removeOutliers ? outlierCells : undefined}
              sortable
            />
          )}
        </Card>
      </div>

      {/* Right: Controls Panel */}
      <div style={{ width: 340, flexShrink: 0 }}>
        <Card
          title={<span style={{ color: '#061b31', fontWeight: 600 }}>数据位置选择</span>}
          style={{ marginBottom: 16 }}
        >
          {/* Task 1: Select All / Deselect All */}
          <Divider orientation="left" style={{ fontSize: 13, margin: '0 0 12px' }}>
            选择列
          </Divider>
          <div style={{ marginBottom: 8 }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              全选 / 取消全选
            </Checkbox>
          </div>
          <Checkbox.Group
            value={selectedCols}
            onChange={(vals) => setSelectedCols(vals as string[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {columns.map((col) => {
              const summary = getColumnSummary(col);
              return (
                <Checkbox key={col.name} value={col.name}>
                  <Tooltip title={summary || col.dtype} placement="left">
                    <span style={{ fontSize: 13 }}>{col.name}</span>
                    <span style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>
                      ({col.dtype})
                    </span>
                    {summary && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#7c4dff',
                          lineHeight: '16px',
                          marginTop: 1,
                          maxWidth: 240,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {summary}
                      </div>
                    )}
                  </Tooltip>
                </Checkbox>
              );
            })}
          </Checkbox.Group>

          {/* X Axis */}
          <Divider orientation="left" style={{ fontSize: 13, margin: '16px 0 12px' }}>
            图形变量
          </Divider>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>X 轴变量：</div>
          <Radio.Group
            value={xAxis}
            onChange={(e) => setXAxis(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {columns.map((col) => (
              <Radio key={col.name} value={col.name} style={{ fontSize: 13 }}>
                {col.name}
              </Radio>
            ))}
          </Radio.Group>

          {/* Y Axis - Numeric only */}
          <div style={{ marginTop: 14, marginBottom: 8, fontSize: 13, color: '#666' }}>
            Y 轴变量：
          </div>
          <Radio.Group
            value={yAxis}
            onChange={(e) => setYAxis(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {numericColumns.length > 0 ? (
              numericColumns.map((col) => (
                <Radio key={col.name} value={col.name} style={{ fontSize: 13 }}>
                  {col.name}
                  <span style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>
                    ({col.dtype})
                  </span>
                </Radio>
              ))
            ) : (
              <span style={{ fontSize: 12, color: '#999' }}>无数值列可选</span>
            )}
          </Radio.Group>

          {/* Group By - Categorical only */}
          <div style={{ marginTop: 14, marginBottom: 8, fontSize: 13, color: '#666' }}>
            分组变量（可选）：
          </div>
          <Radio.Group
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            <Radio value="" style={{ fontSize: 13, color: '#999' }}>
              不分组
            </Radio>
            {categoricalColumns.map((col) => (
              <Radio key={col.name} value={col.name} style={{ fontSize: 13 }}>
                {col.name}
                <span style={{ fontSize: 11, color: '#999', marginLeft: 4 }}>
                  ({col.unique}种)
                </span>
              </Radio>
            ))}
          </Radio.Group>

          {/* Task 6: Missing value handling */}
          <Divider orientation="left" style={{ fontSize: 13, margin: '16px 0 12px' }}>
            缺失值处理
          </Divider>
          <div style={{ marginBottom: 8 }}>
            {missingStats.filter((s) => s.missing > 0).length === 0 ? (
              <div style={{ fontSize: 12, color: '#52c41a' }}>数据无缺失值</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}>
                {missingStats
                  .filter((s) => s.missing > 0)
                  .map((s) => (
                    <div key={s.name} style={{ fontSize: 12, color: '#fa8c16' }}>
                      {s.name}: {s.missing} 缺失 ({s.pct}%)
                    </div>
                  ))}
              </div>
            )}
            <Select
              value={missingStrategy}
              onChange={setMissingStrategy}
              options={MISSING_STRATEGIES}
              style={{ width: '100%' }}
              size="small"
            />
          </div>
        </Card>

        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            size="large"
            block
            onClick={handleNext}
          >
            下一步：选择图表类型
          </Button>
        </Space>
      </div>

      {/* Filter Modal */}
      <Modal
        title="添加筛选条件"
        open={filterModalOpen}
        onOk={addFilter}
        onCancel={() => setFilterModalOpen(false)}
        okText="添加"
        cancelText="取消"
        width={420}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>选择列：</div>
            <Select
              value={newFilterCol}
              onChange={setNewFilterCol}
              style={{ width: '100%' }}
              options={columns.map((c) => ({
                value: c.name,
                label: `${c.name} (${c.dtype})`,
              }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>条件：</div>
            <Select
              value={newFilterOp}
              onChange={setNewFilterOp}
              style={{ width: '100%' }}
              options={OPERATORS.map((op) => ({ value: op, label: op }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>值：</div>
            <Input
              value={newFilterVal}
              onChange={(e) => setNewFilterVal(e.target.value)}
              placeholder="输入筛选值"
              onPressEnter={addFilter}
            />
          </div>
        </div>
      </Modal>

      {/* Task 3: Re-upload Modal */}
      <Modal
        title="重新上传文件"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        footer={null}
        width={480}
      >
        <Upload.Dragger
          accept=".csv,.xlsx,.xls,.tsv"
          showUploadList={false}
          beforeUpload={(file) => {
            handleReUpload(file);
            return false;
          }}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: '#7c4dff' }} />
          </p>
          <p className="ant-upload-text">
            {uploading ? '正在上传...' : '点击或拖拽文件到此区域上传'}
          </p>
          <p className="ant-upload-hint">支持 CSV、Excel 格式文件</p>
        </Upload.Dragger>
      </Modal>

      {/* Chat Input */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          width: '60%',
          maxWidth: 700,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          gap: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 50,
        }}
      >
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="描述您想查看的图表，例如：展示各月份销量趋势"
          onPressEnter={handleChat}
          size="large"
        />
        <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleChat}>
          发送
        </Button>
      </div>
    </div>
  );
};

export default SmartChartDataSelect;
